import React, { useEffect, useState, useRef } from 'react';
import './CompassStateBar.css';

// LED state enum from compass.proto
// TODO: move to protobuf properly, instead of hardcoding here
const LedState = {
  LED_UNDEFINED: 0,
  LED_POWERING_ON: 1,
  LED_POWERING_OFF: 2,
  LED_POWERED_OFF: 3,
  LED_CHARGING: 4,
  LED_CHARGED: 5,
  LED_LID_CLOSED: 6,
  LED_SHOW_TIME: 7,
  LED_SHOW_DIRECTION: 8,
  LED_OFF: 9
};

const CompassStateBar = ({ compassState }) => {
  const [rotation, setRotation] = useState(0);
  const motorGearRef = useRef(null);

  useEffect(() => {
    if (!compassState) return;

    const MOTOR_PROPORTION = 0.5; // Adjust this value as needed
    let lastTime = Date.now();
    let animationFrameId;

    const updateRotation = () => {
      const now = Date.now();
      const elapsed = now - lastTime;
      lastTime = now;

      if (motorGearRef.current) {
        const speed = compassState.motorSpeed || 0;
        const increment = (speed - 90) * MOTOR_PROPORTION * (elapsed / 1000);
        setRotation((prevRotation) => prevRotation + increment);
      }

      animationFrameId = requestAnimationFrame(updateRotation);
    };

    updateRotation();

    return () => cancelAnimationFrame(animationFrameId);
  }, [compassState]);

  if (!compassState) {
    return null;
  }

  const getBatteryStyle = () => {
    const batteryLevel = compassState.powerState?.batteryLevel || 0;
    if (batteryLevel < 10) return 'red';
    if (batteryLevel < 30) return 'yellow';
    return 'green';
  };

  const getTemperatureDisplay = () => {
    const temp = compassState.imuState?.temp;
    if (temp === undefined || temp < -50 || temp > 150) {
      return (
        <span className="crossed-out" style={{ color: 'red' }}>
          🌡️
        </span>
      );
    }
    return `🌡️ ${temp.toFixed(1)}°C`;
  };

  const getLedStateDisplay = () => {
    const ledState = compassState.ledState;
    if (!ledState) return '💡 N/A';
    
    // Convert enum value to string name
    const ledStateName = Object.keys(LedState).find(key => LedState[key] === ledState);
    return `💡 ${ledStateName || 'UNKNOWN'}`;
  };

  // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
  const formatDistance = (distance) => {
    if (distance >= 1000) {
      return `${Math.floor(distance / 1000)} km ${distance % 1000} m`;
    }
    return `${distance} m`;
  };

  const spinSpeedRotation = (speed) => {
    return { transform: `rotate(${speed * 0.5}deg)` };
  };

  const formatNow = (value) => {
    if (!value || value === 0) {
      return (
        <span className="crossed-out" style={{ color: 'red' }}>
          ⏰
        </span>
      );
    }
    const year = value.toString().slice(0, 2);
    const month = value.toString().slice(2, 4);
    const day = value.toString().slice(4, 6);
    const hour = value.toString().slice(6, 8);
    const minute = value.toString().slice(8, 10);
    const date = new Date(`20${year}-${month}-${day}T${hour}:${minute}`);
    return `🕰️ ${date.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}`;
  };

  return (
    <div className="compass-state-bar">
      <div className="top-row">
        <div className="indicator lid">
          {compassState.lidClosed ? '🔒' : '🔓'}
        </div>
        <div className="indicator led">
          {getLedStateDisplay()}
        </div>
        <div className="indicator motor">
          <span ref={motorGearRef} className={`motor-gear ${compassState.disableMotor ? 'crossed-out' : ''}`} style={{ transform: `rotate(${rotation}deg)` }}>⚙️</span>
          {compassState.spinMotor && <span style={spinSpeedRotation(compassState.spinSpeed)}>🔄</span>}
          <span>
            {compassState.motorSpeed}
            {compassState.calibrate && ' 🪛'}
            {compassState.fixDialPosition > -1 && ` 🔨 ${compassState.fixDialPosition}`}
          </span>
        </div>
        <div className="indicator temperature">
          {getTemperatureDisplay()}
        </div>
        <div className="indicator battery">
          <span className="battery-icon" title={`${compassState.powerState?.batteryLevel || 0}% (${compassState.powerState?.batteryVoltage ? compassState.powerState.batteryVoltage.toFixed(2) : 0}V)`}>🔋</span>
          <div className="battery-bar">
            <div className="battery-fill" style={{ width: `${compassState.powerState?.batteryLevel || 0}%`, backgroundColor: getBatteryStyle() }}></div>
            <div className="battery-text">
              {compassState.powerState?.batteryLevel || 0}% ({compassState.powerState?.batteryVoltage ? compassState.powerState.batteryVoltage.toFixed(2) : 0}V)
            </div>
          </div>
        </div>
      </div>
      <div className="bottom-row">
        <div className="indicator imu">
          <span>🧭 Heading: {compassState.imuState?.heading.toFixed(1)}°</span>
          <span>, roll: {compassState.imuState?.roll.toFixed(1)}°</span>
          <span>, pitch: {compassState.imuState?.pitch.toFixed(1)}°</span>
        </div>
        <div className="indicator gnss">
          <span style={{ textDecoration: compassState.gnssState?.hasFix ? 'none' : 'line-through', color: compassState.gnssState?.hasFix ? 'inherit' : 'red' }}>🛰️</span>
          {formatNow(compassState.gnssState?.now)}
        </div>
      </div>
    </div>
  );
};

export default CompassStateBar;
